import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Store, Plus, Pencil, Eye, Phone, MessageCircle, Send, Navigation, Globe,
  LogOut, LayoutDashboard, MessageSquare, Tag, ExternalLink, Loader2, Trash2, Menu,
  BadgeCheck, Search, CheckCircle2, Crown,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { PhotoUploader } from "@/components/PhotoUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "listings", label: "My Listings", icon: Store },
  { id: "claim", label: "Claim Business", icon: BadgeCheck },
  { id: "enquiries", label: "Enquiries", icon: MessageSquare },
  { id: "offers", label: "Offers", icon: Tag },
  { id: "premium", label: "Premium", icon: Crown },
];

const EMPTY = {
  name: "", listing_type: "business", owner_name: "", category_slug: "", city_slug: "etah", address: "", service_area: "", pricing: "", phone: "", whatsapp: "",
  maps_url: "", description: "", services: "", price_range: "₹₹", website: "", instagram: "", facebook: "", photos: [],
};

export default function OwnerDashboard() {
  useSeo("Owner Dashboard | OyeBest", "Manage your business, photos, offers and leads on OyeBest.");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [sidebar, setSidebar] = useState(false);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/service-categories").then((r) => setServiceCategories(r.data));
  }, []);

  const doLogout = async () => { await logout(); navigate("/owner"); };

  return (
    <div className="min-h-screen bg-secondary/40 flex">
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-60 bg-[#0f1115] text-white transform transition-transform ${sidebar ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 border-b border-white/10">
          <Link to="/" className="font-display text-2xl font-extrabold">Oye<span className="text-primary">Best</span></Link>
          <p className="text-xs text-white/40 mt-0.5">Owner Portal</p>
        </div>
        <nav className="p-3 space-y-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => { setSection(n.id); setSidebar(false); }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${section === n.id ? "bg-primary text-white" : "text-white/60 hover:bg-white/10"}`} data-testid={`owner-nav-${n.id}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
          <Link to="/get-your-website" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/60 hover:bg-white/10"><Globe className="w-4 h-4" /> Need a Website?</Link>
        </nav>
        <div className="p-3 absolute bottom-0 inset-x-0 border-t border-white/10">
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/60 hover:bg-white/10" data-testid="owner-logout"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </aside>
      {sidebar && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebar(false)} />}

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white border-b border-border px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setSidebar(true)}><Menu className="w-6 h-6" /></button>
            <h1 className="font-display text-xl font-bold">{NAV.find((n) => n.id === section)?.label}</h1>
          </div>
          <span className="text-sm text-muted-foreground truncate max-w-[40vw]">{user?.email}</span>
        </header>
        <div className="p-4 sm:p-6 max-w-4xl">
          {section === "overview" && <Overview onManage={() => setSection("listings")} />}
          {section === "listings" && <Listings categories={categories} cities={cities} serviceCategories={serviceCategories} />}
          {section === "claim" && <ClaimBusiness />}
          {section === "enquiries" && <Enquiries />}
          {section === "offers" && <Offers />}
          {section === "premium" && <Premium />}
        </div>
      </div>
    </div>
  );
}

function Overview({ onManage }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/owner/stats").then((r) => setS(r.data)); }, []);
  const cards = [
    ["Profile Views", s?.views, Eye], ["Call Clicks", s?.calls, Phone], ["WhatsApp Clicks", s?.whatsapp, MessageCircle],
    ["Contact Now", s?.contact, Send], ["Directions", s?.directions, Navigation], ["Website Clicks", s?.website, Globe],
    ["My Businesses", s?.businesses, Store], ["Enquiries", s?.enquiries, MessageSquare],
  ];
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="owner-stats">
        {cards.map(([l, v, I]) => (
          <div key={l} className="rounded-2xl border border-border bg-white p-5">
            <span className="w-10 h-10 rounded-xl bg-accent text-primary flex items-center justify-center"><I className="w-5 h-5" /></span>
            <p className="text-3xl font-display font-extrabold mt-3">{v ?? "—"}</p>
            <p className="text-sm text-muted-foreground">{l}</p>
          </div>
        ))}
      </div>
      {s && s.businesses === 0 && (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-white p-8 text-center">
          <Store className="w-10 h-10 text-primary mx-auto mb-3" />
          <p className="font-semibold">You haven't added a business yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add your business to start receiving customers and leads.</p>
          <Button className="mt-5 rounded-full" onClick={onManage} data-testid="owner-add-first">Add My Business</Button>
        </div>
      )}
    </>
  );
}

function Listings({ categories, cities, serviceCategories = [] }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/owner/businesses").then((r) => setList(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = (b) => {
    setEditing(b);
    setF(b ? { ...EMPTY, ...b, listing_type: b.listing_type || "business", services: (b.services || []).join(", ") } : EMPTY);
    setOpen(true);
  };
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isService = f.listing_type === "service";
  const catList = isService ? serviceCategories : categories;

  const save = async () => {
    if (!f.name || !f.category_slug || !f.city_slug) { toast.error("Name, category and city required"); return; }
    setSaving(true);
    const payload = { ...f, services: typeof f.services === "string" ? f.services.split(",").map((x) => x.trim()).filter(Boolean) : f.services, menu: f.menu || [] };
    try {
      if (editing) await api.put(`/owner/businesses/${editing.id}`, payload);
      else await api.post("/owner/businesses", payload);
      toast.success(editing ? "Business updated" : "Business submitted for approval");
      setOpen(false); load();
    } catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4"><Button onClick={() => openForm(null)} className="rounded-full gap-2 font-semibold" data-testid="owner-add-business"><Plus className="w-4 h-4" /> Add Listing</Button></div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : list.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground rounded-2xl border border-dashed border-border bg-white">No businesses yet. Add your first one!</div>
      ) : (
        <div className="space-y-3">
          {list.map((b) => (
            <div key={b.id} className="rounded-2xl border border-border bg-white p-4 flex items-center gap-3" data-testid={`owner-biz-${b.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold">{b.name}</span>
                  <Badge className={`rounded-full text-[10px] ${b.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{b.status}</Badge>
                </div>
                <p className="text-sm text-muted-foreground truncate">{b.category_name} · {b.city_name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{(b.stats?.views ?? 0)} views · {(b.stats?.calls ?? 0)} calls · {(b.stats?.contact ?? 0)} enquiries</p>
              </div>
              {b.status === "published" && <Link to={`${b.listing_type === "service" ? "/services" : ""}/${b.city_slug}/${b.category_slug}/${b.slug}`} target="_blank"><Button variant="ghost" size="sm" className="rounded-full gap-1"><ExternalLink className="w-3.5 h-3.5" /></Button></Link>}
              <Button variant="outline" size="sm" onClick={() => openForm(b)} className="rounded-full gap-1" data-testid={`owner-edit-${b.slug}`}><Pencil className="w-3.5 h-3.5" /> Edit</Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="owner-business-form">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "Add"} {isService ? "Service" : "Business"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Fld label="Listing Type">
              <Select value={f.listing_type} onValueChange={(v) => setF((s) => ({ ...s, listing_type: v, category_slug: "" }))}>
                <SelectTrigger className="rounded-xl h-11" data-testid="ob-listing-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="business">Business</SelectItem><SelectItem value="service">Service Provider</SelectItem></SelectContent>
              </Select>
            </Fld>
            <div className="grid sm:grid-cols-2 gap-3">
              <Fld label="Name *"><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl h-11" data-testid="ob-name" /></Fld>
              <Fld label="Owner Name"><Input value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} className="rounded-xl h-11" /></Fld>
              <Fld label="Category *">
                <Select value={f.category_slug} onValueChange={(v) => set("category_slug", v)}><SelectTrigger className="rounded-xl h-11" data-testid="ob-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{catList.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent></Select>
              </Fld>
              <Fld label="City *">
                <Select value={f.city_slug} onValueChange={(v) => set("city_slug", v)}><SelectTrigger className="rounded-xl h-11" data-testid="ob-city"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent></Select>
              </Fld>
              <Fld label="Phone"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-xl h-11" data-testid="ob-phone" /></Fld>
              <Fld label="WhatsApp"><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="rounded-xl h-11" /></Fld>
              <Fld label="Price Range">
                <Select value={f.price_range} onValueChange={(v) => set("price_range", v)}><SelectTrigger className="rounded-xl h-11"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="₹">₹</SelectItem><SelectItem value="₹₹">₹₹</SelectItem><SelectItem value="₹₹₹">₹₹₹</SelectItem></SelectContent></Select>
              </Fld>
              <Fld label="Website"><Input value={f.website} onChange={(e) => set("website", e.target.value)} className="rounded-xl h-11" /></Fld>
            </div>
            <Fld label="Address"><Input value={f.address} onChange={(e) => set("address", e.target.value)} className="rounded-xl h-11" data-testid="ob-address" /></Fld>
            {isService && (
              <div className="grid sm:grid-cols-2 gap-3">
                <Fld label="Service Area"><Input value={f.service_area} onChange={(e) => set("service_area", e.target.value)} className="rounded-xl h-11" data-testid="ob-service-area" /></Fld>
                <Fld label="Pricing (optional)"><Input value={f.pricing} onChange={(e) => set("pricing", e.target.value)} className="rounded-xl h-11" placeholder="e.g. From ₹199 / visit" data-testid="ob-pricing" /></Fld>
              </div>
            )}
            <Fld label="Google Maps URL"><Input value={f.maps_url} onChange={(e) => set("maps_url", e.target.value)} className="rounded-xl h-11" /></Fld>
            <Fld label="Description"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl" rows={2} data-testid="ob-desc" /></Fld>
            <Fld label="Services (comma separated)"><Input value={f.services} onChange={(e) => set("services", e.target.value)} className="rounded-xl h-11" /></Fld>
            <div className="grid sm:grid-cols-2 gap-3">
              <Fld label="Instagram"><Input value={f.instagram} onChange={(e) => set("instagram", e.target.value)} className="rounded-xl h-11" /></Fld>
              <Fld label="Facebook"><Input value={f.facebook} onChange={(e) => set("facebook", e.target.value)} className="rounded-xl h-11" /></Fld>
            </div>
            <Fld label="Photos"><PhotoUploader value={f.photos} onChange={(v) => set("photos", v)} uploadPath="/owner/upload" /></Fld>
            <Button onClick={save} disabled={saving} className="w-full rounded-full h-12 font-semibold gap-2" data-testid="ob-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Save Changes" : "Submit for Approval"}</Button>
            {!editing && <p className="text-xs text-muted-foreground text-center">New listings are reviewed by admin before publishing.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Enquiries() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/owner/enquiries").then((r) => setList(r.data)).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (list.length === 0) return <div className="text-center py-16 text-muted-foreground rounded-2xl border border-dashed border-border bg-white">No enquiries yet. They'll appear here when customers use "Contact Now".</div>;
  return (
    <div className="space-y-3">
      {list.map((e) => (
        <div key={e.id} className="rounded-2xl border border-border bg-white p-4" data-testid={`owner-enq-${e.id}`}>
          <p className="font-bold">{e.name} <a href={`tel:${e.mobile}`} className="font-normal text-primary">· {e.mobile}</a></p>
          <p className="text-sm text-muted-foreground">For: {e.business_name}</p>
          {e.message && <p className="text-sm mt-1">{e.message}</p>}
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</p>
            <a href={`https://wa.me/91${e.mobile}`} target="_blank" rel="noreferrer"><Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700 gap-1"><MessageCircle className="w-3.5 h-3.5" /> Reply</Button></a>
          </div>
        </div>
      ))}
    </div>
  );
}

function Offers() {
  const [list, setList] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ business_id: "", title: "", description: "", discount: "", start_date: "", end_date: "", terms: "" });
  const load = () => api.get("/owner/offers").then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/owner/businesses").then((r) => setBusinesses(r.data)); }, []);
  const save = async () => {
    if (!f.business_id || !f.title) { toast.error("Business and title required"); return; }
    await api.post("/owner/offers", f); toast.success("Offer submitted for approval"); setOpen(false);
    setF({ business_id: "", title: "", description: "", discount: "", start_date: "", end_date: "", terms: "" }); load();
  };
  const del = async (id) => { await api.delete(`/owner/offers/${id}`); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button onClick={() => setOpen(true)} disabled={businesses.length === 0} className="rounded-full gap-2 font-semibold" data-testid="owner-add-offer"><Plus className="w-4 h-4" /> Add Offer</Button></div>
      {list.length === 0 ? <div className="text-center py-16 text-muted-foreground rounded-2xl border border-dashed border-border bg-white">No offers yet.</div> : (
        <div className="space-y-3">
          {list.map((o) => (
            <div key={o.id} className="rounded-2xl border border-border bg-white p-4 flex items-center justify-between" data-testid={`owner-offer-${o.id}`}>
              <div><p className="font-bold">{o.title} {o.discount && <span className="text-primary">· {o.discount}</span>}</p><p className="text-sm text-muted-foreground">{o.business_name} · <Badge className={`rounded-full text-[10px] ${o.status === "approved" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{o.status}</Badge></p></div>
              <Button variant="ghost" size="sm" onClick={() => del(o.id)} className="rounded-full text-red-600"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Add Offer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Fld label="Business">
              <Select value={f.business_id} onValueChange={(v) => setF({ ...f, business_id: v })}><SelectTrigger className="rounded-xl h-11" data-testid="oo-business"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select>
            </Fld>
            <Fld label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-xl h-11" data-testid="oo-title" /></Fld>
            <Fld label="Discount"><Input value={f.discount} onChange={(e) => setF({ ...f, discount: e.target.value })} className="rounded-xl h-11" placeholder="e.g. 20% OFF" /></Fld>
            <Fld label="Description"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-xl" rows={2} /></Fld>
            <Button onClick={save} className="w-full rounded-full font-semibold" data-testid="oo-save">Save Offer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fld({ label, children }) { return <div className="space-y-1.5"><Label className="text-sm font-semibold">{label}</Label>{children}</div>; }

function Premium() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/owner/subscription").then((r) => setData(r.data)); }, []);
  const upgrade = async (businessId, planId) => {
    setBusy(businessId + planId);
    try {
      const { data: res } = await api.post("/payments/checkout", { plan_id: planId, business_id: businessId, origin_url: window.location.origin });
      window.location.href = res.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setBusy(""); }
  };
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  return (
    <div className="space-y-6" data-testid="owner-premium">
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5">
        <div className="flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" /><p className="font-display text-lg font-bold">OyeBest Premium</p><span className="text-[10px] rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 font-bold">PAID MEMBERSHIP</span></div>
        <p className="text-sm text-muted-foreground mt-1">Basic listing hamesha free hai. Premium se paayein extra visibility aur badge.</p>
        <ul className="mt-3 grid sm:grid-cols-2 gap-1.5 text-sm">{data.benefits.map((b) => <li key={b} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-600" /> {b}</li>)}</ul>
      </div>

      {data.businesses.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground rounded-2xl border border-dashed border-border bg-white">Add a business first to upgrade it to Premium.</div>
      ) : data.businesses.map((b) => (
        <div key={b.id} className="rounded-2xl border border-border bg-white p-5" data-testid={`owner-premium-biz-${b.id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div><p className="font-bold">{b.name}</p>
              <p className="text-sm text-muted-foreground">Status: <span className={`font-semibold ${b.premium ? "text-green-600" : "text-muted-foreground"}`}>{b.premium ? "Premium Active" : (b.premium_status === "expired" ? "Expired" : "Free plan")}</span>
                {b.premium && b.premium_expiry && <> · Expires {new Date(b.premium_expiry).toLocaleDateString()}</>}
              </p>
            </div>
            {b.premium && <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-yellow-400 text-white font-bold px-3 py-1 text-xs"><Crown className="w-3.5 h-3.5" /> Premium</span>}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {data.plans.map((p) => (
              <div key={p.id} className="rounded-xl border border-border p-4 flex items-center justify-between">
                <div><p className="font-semibold">{p.label}</p><p className="text-lg font-display font-extrabold">₹{p.amount}<span className="text-xs font-normal text-muted-foreground">/{p.interval}</span></p></div>
                <Button size="sm" disabled={busy === b.id + p.id} onClick={() => upgrade(b.id, p.id)} className="rounded-full gap-1" data-testid={`upgrade-${b.id}-${p.id}`}>
                  {busy === b.id + p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />} {b.premium ? "Renew" : "Upgrade"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ClaimBusiness() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ phone: "", message: "" });

  const loadClaims = () => api.get("/owner/claims").then((r) => setClaims(r.data));
  const search = useCallback(() => {
    setLoading(true);
    api.get("/owner/claimable", { params: { q } }).then((r) => setResults(r.data)).finally(() => setLoading(false));
  }, [q]);
  useEffect(() => { loadClaims(); }, []);
  useEffect(() => { search(); }, []); // eslint-disable-line

  const openClaim = (b) => { setTarget(b); setForm({ phone: b.phone || "", message: "" }); setOpen(true); };
  const submit = async () => {
    try { await api.post("/owner/claims", { business_id: target.id, ...form }); toast.success("Claim submitted for admin review"); setOpen(false); loadClaims(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit claim"); }
  };
  const pendingIds = new Set(claims.filter((c) => c.status === "pending").map((c) => c.business_id));

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">Apna existing business dhoondhiye aur claim kijiye. Admin verify karke aapko manage karne ka access dega.</p>
      <form onSubmit={(e) => { e.preventDefault(); search(); }} className="flex items-center bg-white rounded-full border border-border px-3 h-11 max-w-md mb-5">
        <Search className="w-4 h-4 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a business to claim…" className="bg-transparent outline-none px-2 text-sm w-full" data-testid="claim-search-input" />
      </form>

      {claims.length > 0 && (
        <div className="mb-6">
          <p className="font-semibold mb-2">My Claims</p>
          <div className="space-y-2">
            {claims.map((c) => (
              <div key={c.id} className="rounded-xl border border-border bg-white p-3 flex items-center justify-between" data-testid={`my-claim-${c.id}`}>
                <span className="font-medium text-sm">{c.business_name}</span>
                <Badge className={`rounded-full text-[10px] ${c.status === "approved" ? "bg-green-100 text-green-700" : c.status === "rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{c.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="font-semibold mb-2">Claimable Businesses</p>
      {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : results.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground rounded-2xl border border-dashed border-border bg-white">No unclaimed businesses found.</div>
      ) : (
        <div className="space-y-3">
          {results.map((b) => (
            <div key={b.id} className="rounded-2xl border border-border bg-white p-4 flex items-center gap-3" data-testid={`claimable-${b.slug}`}>
              <div className="flex-1 min-w-0">
                <p className="font-bold flex items-center gap-2">{b.name} {b.is_demo && <Badge variant="outline" className="rounded-full text-[10px]">Demo</Badge>}</p>
                <p className="text-sm text-muted-foreground truncate">{b.category_name} · {b.city_name} · {b.address}</p>
              </div>
              {pendingIds.has(b.id)
                ? <Badge className="rounded-full bg-amber-100 text-amber-700 text-[10px]">Claim Pending</Badge>
                : <Button size="sm" onClick={() => openClaim(b)} className="rounded-full gap-1" data-testid={`claim-btn-${b.slug}`}><BadgeCheck className="w-3.5 h-3.5" /> Claim</Button>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Claim {target?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Verify karne ke liye apni details dijiye. Admin approve karega.</p>
            <Fld label="Your Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl h-11" data-testid="claim-phone" /></Fld>
            <Fld label="Message (proof of ownership)"><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl" rows={3} data-testid="claim-message" /></Fld>
            <Button onClick={submit} className="w-full rounded-full font-semibold" data-testid="claim-submit">Submit Claim</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
